import type {Edge, Node, NodeHandle} from "@xyflow/react";
import {Position} from "@xyflow/react";
import {hierarchy, tree} from "d3-hierarchy";
import type {m_taxonomy} from "~/services/classes/TaxonomyClasses";

/** Card size of a category on the canvas. */
export const NODE_WIDTH = 260;
export const NODE_HEIGHT = 80;
const ROW_GAP = 16;
const COLUMN_GAP = 100;

export type TaxonomyTreeNodeData = {
    label: string;
    /** 0 = the taxonomy itself, 1 = L1, … */
    level: number;
    childCount: number;
    expanded: boolean;
    /** Chosen in the level filters */
    focused?: boolean;
    /** Opens the add dialog for a category under this one; unset when the user can't edit. */
    onAdd?: () => void;
    /** Opens the delete confirmation; unset for the root and when the user can't edit. */
    onDelete?: () => void;
    /** Opens the rename confirmation for a new name; unset for the root and when the user can't edit. */
    onRename?: (label: string) => void;
    /** Opens the same dialog with the name unchanged, to edit only the description; unset like `onRename`. */
    onEditDescription?: () => void;
    /** Set while a dragged card hovers this one: whether the dragged category can move under it. */
    dropTarget?: 'valid' | 'invalid';
    /** Set with `dropTarget`: how much larger the card is drawn, so it stands out when zoomed out. */
    dropTargetScale?: number;
    /** Set in merge mode on every category: its checkbox. `disabled` when it isn't a sibling of the chosen ones. */
    mergeSelect?: {checked: boolean, disabled: boolean, onToggle: () => void};
};
export type TaxonomyTreeNode = Node<TaxonomyTreeNodeData, 'taxonomy'>;

/**
 * Edge anchors declared up front (left middle in, right middle out), so React Flow doesn't have to
 * measure the handles and never loses them when a node object is replaced.
 */
const HANDLES: NodeHandle[] = [
    {type: 'target', position: Position.Left, x: 0, y: NODE_HEIGHT / 2},
    {type: 'source', position: Position.Right, x: NODE_WIDTH, y: NODE_HEIGHT / 2},
];

export const visibleChildren = (n: m_taxonomy.Tree) => n.children.filter(c => !c.removed);

/** Lays the taxonomy out left to right; only children of expanded nodes are shown. */
export function layoutTaxonomyTree(
    root: m_taxonomy.Tree,
    rootLabel: string,
    expanded: ReadonlySet<number>,
): {nodes: TaxonomyTreeNode[], edges: Edge[]} {
    // d3 lays out top-down; swap the axes for a left-to-right tree.
    const h = tree<m_taxonomy.Tree>().nodeSize([NODE_HEIGHT + ROW_GAP, NODE_WIDTH + COLUMN_GAP])(
        hierarchy(root, n => expanded.has(n.id) ? visibleChildren(n) : []),
    );

    const nodes: TaxonomyTreeNode[] = h.descendants().map(d => ({
        id: String(d.data.id),
        type: 'taxonomy',
        position: {x: d.y, y: d.x},
        // Fixed size, so fitting and the minimap work without measuring.
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
        handles: HANDLES,
        data: {
            label: d.depth === 0 ? rootLabel : d.data.label,
            level: d.depth,
            childCount: visibleChildren(d.data).length,
            expanded: expanded.has(d.data.id),
        },
    }));
    const edges: Edge[] = h.links().map(l => ({
        id: `${l.source.data.id}-${l.target.data.id}`,
        source: String(l.source.data.id),
        target: String(l.target.data.id),
        type: 'smoothstep',
    }));
    return {nodes, edges};
}
